import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next/pages';
import { match } from 'ts-pattern';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { backgroundColorInput } from '@/shared/inputs';
import {
  getBlockColor,
  getBlockIndex,
  getBlocks,
  getBuilderSpacingClass,
  getPreviousBlock,
} from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { APP_STORE_RESOURCES } from './assets';
import style from './DownloadApps.module.scss';

type Props = SharedBuilderComponent & {
  locale: string;
};

export function DownloadApps({
  attributes,
  builderBlock,
  builderState,
  locale,
}: Props) {
  const { t } = useTranslation();
  const blocks = getBlocks(builderState?.content.data.blocks || []);
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = match({
    index: getBlockIndex(builderBlock, builderState),
  })
    .with({ index: blocks.length - 1 }, () => {
      const previousBlock = getPreviousBlock(builderBlock, builderState);
      if (getBlockColor(previousBlock) === getBlockColor(builderBlock)) {
        return '';
      }
      return `u-p-t-lg-xxl-18 u-p-t-xxl-6`;
    })
    .otherwise(() => getBuilderSpacingClass(builderBlock, builderState));

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  return (
    <section {...attributes} className={cn(builderStyles, spacingStyles)}>
      <div className="u-wrapper">
        <div className={style.banner}>
          <div className={style.container}>
            <h3 className="u-typography-h3">
              {t('download_catawiki_app_header')}
            </h3>
            <h4 className={cn(style.subHeader, 'u-typography-h4')}>
              {t('download_catawiki_app_subheader')}
            </h4>
            <p className={style.links}>
              <a
                title={t('download_our_apps_from_apple') ?? ''}
                href="https://app.adjust.com/v8wodbh_owm7xlg"
              >
                <img
                  alt={t('download_our_apps_from_apple') ?? ''}
                  src={APP_STORE_RESOURCES[blockLocale].apple}
                />
              </a>
              <a
                title={t('download_our_apps_from_google') ?? ''}
                href="https://app.adjust.com/v8wodbh_owm7xlg"
              >
                <img
                  alt={t('download_our_apps_from_google') ?? ''}
                  src={APP_STORE_RESOURCES[blockLocale].google}
                />
              </a>
            </p>
          </div>
          <picture className={style.images}>
            <source
              media="(min-width: 996px)"
              srcSet="https://cw-app-marketing.s3.amazonaws.com/assets/landing-pages/landing-pages/no-rp-day-2021/download-app.jpg"
            />
            <source
              media="(max-width: 995px)"
              srcSet="https://cdn.catawiki.net/assets/buyer/ui/v2/download-banner/banner-portrait-627185bf6635c8a54d999fbee5a4def2c599f57d2715212efd82d3cddacc45e4.jpg"
            />
            <img
              className={style.image}
              alt="Download apps"
              src="https://cw-app-marketing.s3.amazonaws.com/assets/landing-pages/landing-pages/no-rp-day-2021/download-app.jpg"
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'DownloadApps';

export function componentRegister() {
  Builder.registerComponent(DownloadApps, {
    name: `v2:${NAME}`,
    inputs: [backgroundColorInput(COLOR_NAME_WHITE, { isHidden: true })],
    noWrap: true,
  });
  return `v2:${NAME}`;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
